import classNames from 'classnames'
import { ItemListElements } from 'next-seo/lib/types'

import SeoContainer, {
  SeoContainerProps,
} from 'components/util/containers/SeoContainer'
import Strings from 'project/localisation'
import { sectionGap } from 'project/padding'
import Button from 'components/forms/Button'
import useWindowSize from 'components/util/useWindowSize'
import HMPlusTide from 'components/icons/HMPlusTide'
import HMPlusTideMobile from 'components/icons/HMPlusTideMobile'
import { NextPageWithLayout } from '../types/nextPageWithLayout'
import PartnerHeroBanner from 'components/hero/PartnerHeroBanner'
import LeftRobotCard from 'components/cards/new/common/LeftRobotCard'
import { homeBreadcrumb } from 'components/util/containers/Breadcrumbs'
import FreeBusinessBanking from 'components/cards/new/FreeBusinessBanking'
import TideExclusiveOfferCard from 'components/cards/new/TideExclusiveOfferCard'
import { businessBankingStrings } from 'common/strings/business-banking-strings'
import TideExclusiveBusinessPartner from 'components/cards/new/TideExclusiveBusinessPartner'
import { useEffect, useState } from 'react'
import { AddonSlug, PlanSlug } from '../common/planSlugs'
import { I18NConfig } from 'next/dist/server/config-shared'
import { getAddons, getPlans } from '../common/planData'
import { TAddon, TPlanSummary } from '../common/cms-types'
import { useRouter } from 'next/router'
import useShoppingBag from '../common/useShoppingBag'
import { ButtonType } from '../types/global'

export type TideType = {
  addons: TAddon[]
  plans: TPlanSummary[]
}
const TidePage: NextPageWithLayout<TideType> = ({ addons, plans }) => {
  const size = useWindowSize()
  const router = useRouter()
  const [shouldAddAddon, setShouldAddAddon] = useState(false)
  const { addPlan, addPlanAddon, value } = useShoppingBag()
  const VOPlan = plans.find((v) => v.slug === PlanSlug.SCAN_LITE)

  const addon = addons.find(
    (addon) => addon.slug === AddonSlug.TIDE_BUSINESS_BANKING,
  )

  useEffect(() => {
    if (shouldAddAddon && value.slug) {
      if (addon) {
        addPlanAddon(addon.slug, addon)
        setShouldAddAddon(false)
        router.push('/checkout')
      }
    }
  }, [shouldAddAddon, value])

  const seoProps: SeoContainerProps['seoProps'] = {
    description: businessBankingStrings.seoBusinessBankingDescription,
    openGraph: {
      url: 'https://www.hoxtonmix.com/free-business-banking/',
    },
    title: businessBankingStrings.seoBusinessBankingTitle,
  }
  const breadcrumb: ItemListElements[] = [
    homeBreadcrumb()[0],
    {
      item: 'https://www.hoxtonmix.com/free-business-banking/',
      name: `${Strings.freeBusinessBanking}`,
      position: 2,
    },
  ]
  const handleSignUp = () => {
    if (addon && VOPlan) {
      addPlan(VOPlan?.slug, 'year', VOPlan, ButtonType.BUSINESS_BANKING_PAGE_HERO)
      setShouldAddAddon(true)
    }
  }
  return (
    <SeoContainer breadcrumb={breadcrumb} seoProps={seoProps}>
      <main className={'overflow-x-hidden new-design d-flex flex-column'}>
        <PartnerHeroBanner
          partnershipLogo={
            <div className='d-flex justify-content-center justify-content-lg-start align-items-start'>
              <HMPlusTideMobile className='mb-5 d-lg-none' />
              <HMPlusTide className='mb-lg-5 d-none d-lg-block max-w-100' />
            </div>
          }
          title='A Free Business Bank Account in Minutes'
          robotImage={{
            height: 824,
            src: '/images/new/tide/robot-holding-tide-card.png',
            width: 1052,
          }}
          robotImageMobile={{
            height: 640,
            src: '/images/new/tide/robot-pointing-tide-card.png',
            width: 532,
          }}
          imageContainerWidth={size.isLgOrHigher ? '60%' : '100%'}
          description={
            <span>
              Open a free business bank account in {size.isLgOrHigher && <br />}
              minutes — and get {businessBankingStrings.tideCashBackAmount} cashback with Tide!
            </span>
          }
          contentContainerStyle={{
            marginRight: size.isLgOrHigher ? '-40%' : '0',
          }}
          ctaBtn={
            <>
              <Button
                onClick={handleSignUp}
                className='w-100 w-lg-auto align-self-lg-start mt-7'
              >
                Open an account
              </Button>
            </>
          }
        />
        <div className={classNames('container d-flex flex-column', sectionGap)}>
          <div className='container d-flex flex-column pt-lg-0 pt-7 gap-12 gap-lg-25'>
            <TideExclusiveOfferCard />
            <h2 className='fs-fluid-36-48 fw-semibold lh-105 lh-lg-114 mb-0 text-center'>
              An overview of Tide's account features:
            </h2>

            <LeftRobotCard
              image={{
                alt: 'Robot pointing to Tide card',
                desktop: {
                  dimensions: {
                    height: 623,
                    width: 518,
                  },
                  src: '/images/new/tide/robot-pointing-tide-card.png',
                },
                mobile: {
                  dimensions: {
                    height: 623,
                    width: 518,
                  },
                  src: '/images/new/tide/robot-pointing-tide-card.png',
                },
              }}
              imageContainerWidth={size.isLgOrHigher ? '41%' : '100%'}
              imageStyle={{
                bottom: 0,
                maxWidth: '100%',
                objectFit: 'contain',
              }}
              reverseColumnOrder={!size.isLgOrHigher}
            >
              <div className='d-flex flex-1 flex-column pt-9 px-4 py-lg-14 pe-lg-17'>
                <div className='fw-semibold lh-114 mb-1 fs-fluid-24-34 d-flex flex-column gap-lg-4 gap-2 pb-6 pb-lg-6'>
                  <span className='d-block'>
                    <strong className='color-pink2'>Free</strong> sign-up — no
                    monthly or annual fees{' '}
                  </span>
                  <span className='d-block'>
                    <strong className='color-pink2'>Free</strong> Mastercard use
                    — at home and abroad
                  </span>
                </div>
                {/*fs is always 22*/}
                <span className='fw-semibold lh-123 fs-22 '>
                  Order up to 35 additional Mastercards for your team.
                </span>
                <div className='lh-140 fs-fluid-18-22 pt-2'>
                  Make VAT returns a breeze with easy expense management —
                  upload receipts, auto-match transactions, and add notes and
                  categories.
                  <br />
                  <br /> 24/7 in-app support.
                  <br />
                  <br />
                  Mobile-first, but not mobile-only — manage your account your
                  way, from your phone or computer. <br />
                  <br /> Access direct feeds with Xero, FreeAgent, Sage,
                  QuickBooks, and KashFlow to help manage your accounts.
                </div>
              </div>
            </LeftRobotCard>
          </div>

          <FreeBusinessBanking />
          <TideExclusiveBusinessPartner />
        </div>
      </main>
    </SeoContainer>
  )
}

export async function getStaticProps(
  context: I18NConfig & { locale?: string; params: { uid: string } },
) {
  try {
    const addons = await getAddons(true)
    const plans = await getPlans()

    return {
      props: {
        addons,
        plans,
      },
    }
  } catch (e) {
    return {
      props: {},
    }
  }
}

export default TidePage
